import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild, OnChanges, HostListener } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { Observable, BehaviorSubject } from 'rxjs';
import { debounceTime, map, startWith } from 'rxjs/operators';


export interface TagsAutocompleteOptions {
  placeholder: string;
  label: string;
  appearance: MatFormFieldAppearance;
  addTagText: string;
  addTag: boolean;
  selectable: boolean;
  removable: boolean;
  disabled: boolean;
}

export type TagsAutocompleteOptionsPartial = Partial<TagsAutocompleteOptions>;
export interface AutocompleteTag {
  id?: number | string;
  name: string;
  highlight?: boolean;
  custom?: boolean;
  isCategory?: boolean;
  category?: string;
}

const DEFAULT_TAG_AUTOCOMPLETE_OPTIONS: TagsAutocompleteOptions = {
  placeholder: 'Document Type...',
  label: 'Document Type',
  appearance: 'outline',
  addTagText: 'Create New Type',
  addTag: true,
  selectable: false,
  removable: true,
  disabled: false
};

@Component({
    selector: 'app-tags-autocomplete',
    templateUrl: './tags-autocomplete.component.html',
    styleUrls: ['./tags-autocomplete.component.scss'],
    standalone: false
})
export class TagsAutocompleteComponent implements OnInit {
  @Input() config: TagsAutocompleteOptionsPartial = {};
  @Input() defaultTags: AutocompleteTag[] = [];
  @Input() tags: AutocompleteTag[] = [];
  @Input() isError: boolean = false;
  @Input() errorMessage: string = '';
  @Output() change = new EventEmitter<AutocompleteTag[]>();

  separatorKeysCodes: number[] = [ENTER, COMMA];
  tagCtrl = new UntypedFormControl('');
  filteredTags: Observable<AutocompleteTag[]>;
  options: TagsAutocompleteOptions = { ...DEFAULT_TAG_AUTOCOMPLETE_OPTIONS, ...this.config };
  private selectedTagsSubject = new BehaviorSubject<AutocompleteTag[]>([]);
  selectedTags$ = this.selectedTagsSubject.asObservable();

  @ViewChild('tagInput') tagInput: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) matAutocompleteTrigger: MatAutocompleteTrigger;

  constructor() {
    this.filteredTags = this.tagCtrl.valueChanges.pipe(
      debounceTime(300),
      startWith(''),
      map((inputText: string | null) => this._filterTags(inputText))
    );

    this.selectedTags$.subscribe(tags => this.change.emit(tags));
  }

  ngOnInit(): void {
    if (this.config.disabled) {
      this.tagCtrl.disable();
      this.config.removable = false;
    } else {
      this.tagCtrl.enable();
    }
    this.updateOptions();
  }

  ngAfterViewInit() {
    this.matAutocompleteTrigger.panelClosingActions.subscribe(event => {
      if (this.config.disabled) return;
      if (event) {
        setTimeout(() => this.matAutocompleteTrigger.openPanel());
      }
    });
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: Event): void {
    if (this.config.disabled) return;
    if (!this.tagInput.nativeElement.contains(event.target as Node)) {
      this.matAutocompleteTrigger.closePanel();
    }
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    const currentTags = this.selectedTagsSubject.getValue();
    if (value) {
      const existingTag = this.tags.find(tag => tag.name.toLowerCase() === value.toLowerCase());
      const isTagSelected = this.isTagSelected(value);
      if (isTagSelected) {
        // Highlight existing tag
        this.highlightTag(value);
      } else {
        const newSelectedTags = [...currentTags, existingTag ?? { id: value, name: value, custom: true }];
        this.selectedTagsSubject.next(newSelectedTags);
      }
    }
    event.chipInput!.clear();
    this.tagCtrl.setValue(null);
  }

  remove(tagId: AutocompleteTag['id']): void {
    const newSelectedTags = this.selectedTagsSubject.getValue().filter(tag => tag.id !== tagId);
    this.selectedTagsSubject.next(newSelectedTags);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tags'] && changes['tags'].currentValue) {
      this.updateOptions();
      this.filteredTags = this.tagCtrl.valueChanges.pipe(
        debounceTime(300),
        startWith(''),
        map((inputText: string | null) => this._filterTags(inputText))
      );
    }

    if (changes['defaultTags'] && changes['defaultTags'].currentValue) {
      const defaultTags = changes['defaultTags'].currentValue as AutocompleteTag[];
      const defaultTagIds = defaultTags.map(tag => tag.id);
      const selectedTags = this.tags.filter(tag => defaultTagIds.includes(tag.id));
      this.selectedTagsSubject.next(selectedTags);
    }
  }

  private updateOptions(): void {
    this.options = { ...DEFAULT_TAG_AUTOCOMPLETE_OPTIONS, ...this.config };
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const tagId = event.option.value as AutocompleteTag['id'];
    if (!this.isTagSelected(tagId)) {
      const selectedTag = this.tags.find(tag => tag.id === tagId);
      const newSelectedTags = [...this.selectedTagsSubject.getValue(), selectedTag ?? { id: tagId, name: tagId as string, custom: true }];
      this.selectedTagsSubject.next(newSelectedTags);
    } else {
      this.remove(tagId);
      // Highlight existing tag
      // this.highlightTag(tagId);
    }
    this.tagInput.nativeElement.value = '';
    this.tagCtrl.setValue(null);
  }

  private _filterTags(text: string | null): AutocompleteTag[] {
    if (!this.tags || this.tags.length === 0) return [];
    const filterValue = text?.toLowerCase() || '';
    const result: AutocompleteTag[] = [];
    let currentCategory: AutocompleteTag | null = null;
    let categoryTags: AutocompleteTag[] = [];
    let uncategorizedTags: AutocompleteTag[] = [];
    let seenCategory = false;

    const pushCategoryIfMatches = () => {
      if (currentCategory && categoryTags.length > 0) {
        result.push(currentCategory, ...categoryTags);
      }
      categoryTags = [];
    };

    for (const tag of this.tags) {
      if (tag.isCategory) {
        pushCategoryIfMatches();
        currentCategory = tag;
        seenCategory = true;
      } else if (tag.name.toLowerCase().includes(filterValue)) {
        if (currentCategory) {
          categoryTags.push(tag);
        } else {
          uncategorizedTags.push(tag);
        }
      }
    }
    pushCategoryIfMatches(); // Handle last category
    // Add uncategorized tags at the top if any
    if (uncategorizedTags.length > 0) {
      result.unshift(...uncategorizedTags);
    }

    return result;
  }

  private isTagSelected(value: string | number): boolean {
    const lowerValue = typeof value === 'string' ? value.toLowerCase() : value;
    return this.selectedTagsSubject.getValue().some(tag =>
      (typeof tag.id === 'string' ? tag.id.toLowerCase() : tag.id) === lowerValue || tag.name.toLowerCase() === lowerValue
    );
  }

  getPlaceholder(): string {
    return this.selectedTagsSubject.getValue().length ? '' : this.options.placeholder;
  }

  private highlightTag(value: string | number): void {
    const lowerValue = typeof value === 'string' ? value.toLowerCase() : value;
    const currentTags = this.selectedTagsSubject.getValue().map(tag => {
      if ((typeof tag.id === 'string' ? tag.id.toLowerCase() : tag.id) === lowerValue || tag.name.toLowerCase() === lowerValue) {
        return { ...tag, highlight: true };
      }
      return tag;
    });
    this.selectedTagsSubject.next(currentTags);

    // Remove highlight after 3 seconds
    setTimeout(() => {
      const updatedTags = this.selectedTagsSubject.getValue().map(tag => {
        if ((typeof tag.id === 'string' ? tag.id.toLowerCase() : tag.id) === lowerValue || tag.name.toLowerCase() === lowerValue) {
          return { ...tag, highlight: false };
        }
        return tag;
      });
      this.selectedTagsSubject.next(updatedTags);
    }, 3000);
  }

  onMatFormFieldClick(event: MouseEvent): void {
    if (this.config.disabled) return;
    event.stopPropagation();
    this.matAutocompleteTrigger.openPanel();
  }
}
